/**
 * Logo da empresa (seção de Configurações > Minha empresa): o logo que sai
 * no cabeçalho das folhas impressas (apontamentos por lote).
 *
 * Cada unidade tem o próprio logo (o ERP é vendido a outras marcenarias);
 * sem logo, as folhas mostram o nome da unidade. Todo mundo vê; só quem tem
 * `unidade:editar_logo` troca. Quem decide é o backend (`pode_editar`).
 */
import React, { useRef } from "react";

import AsyncButton from "../../../components/AsyncButton";
import useLogoUnidade from "../../../hooks/useLogoUnidade";

const TIPOS_ACEITOS = "image/png,image/jpeg,image/webp,image/svg+xml";
const TAMANHO_MAXIMO_MB = 2;

const estiloTexto = { fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5, margin: 0 };
const estiloAmostra = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 96,
  padding: 16,
  border: "1px solid var(--border)",
  borderRadius: 10,
  background: "#fff",
};

export default function LogoDaEmpresa() {
  const { logo, nomeUnidade, podeEditar, carregado, erro, enviar, remover } = useLogoUnidade();
  const entrada = useRef(null);

  const escolher = async () => {
    const arquivo = entrada.current?.files?.[0];
    if (!arquivo) throw new Error("Escolha uma imagem primeiro.");
    if (arquivo.size > TAMANHO_MAXIMO_MB * 1024 * 1024) throw new Error(`A imagem passa de ${TAMANHO_MAXIMO_MB} MB.`);
    await enviar(arquivo);
    entrada.current.value = "";
  };

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">Logo</h2>
        <p className="set-section__desc">
          Logo que aparece no cabeçalho das folhas de apontamento impressas{nomeUnidade ? ` de ${nomeUnidade}` : ""}.
        </p>
      </div>
      <div className="set-section__body" style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 560 }}>
        {erro && <p style={{ ...estiloTexto, color: "var(--danger-text)" }}>{erro}</p>}
        {carregado && (
          <div style={estiloAmostra} aria-label="Como o logo sai na folha">
            {logo
              ? <img src={logo} alt={`Logo de ${nomeUnidade}`} style={{ height: 40, width: "auto", maxWidth: 170, objectFit: "contain" }} />
              : <span style={{ fontSize: 13, color: "#6F6A66" }}>Sem logo: a folha mostra “{nomeUnidade || "o nome da unidade"}”.</span>}
          </div>
        )}
        <p style={estiloTexto}>
          PNG, JPG, WEBP ou SVG, até {TAMANHO_MAXIMO_MB} MB. De preferência com fundo transparente e mais largo que alto;
          ele sai com 40 px de altura na folha.
        </p>
        {podeEditar ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <input ref={entrada} type="file" accept={TIPOS_ACEITOS} aria-label="Arquivo do logo" style={{ fontSize: 13, maxWidth: "100%" }} />
            <AsyncButton variant="primary" onClick={escolher} successMessage="Logo atualizado.">Enviar logo</AsyncButton>
            {logo && (
              <AsyncButton variant="secondary" onClick={remover} successMessage="Logo removido.">Remover</AsyncButton>
            )}
          </div>
        ) : (
          <p style={{ ...estiloTexto, color: "var(--ink-3)" }}>Só quem tem a permissão “Trocar o logo da unidade” pode alterar.</p>
        )}
      </div>
    </section>
  );
}
